import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, ChevronDown, ChevronUp, Award, Clock, BookOpen } from 'lucide-react';
import { vocationalPrograms, vocationalCategories, vocationalStats } from '../data/vocationalData';
import { SEO, pageSEO } from '../components/SEO';

export default function Vocational() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPrograms = vocationalPrograms.filter(p => {
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || p.level === selectedLevel;
    const matchesSearch = searchQuery === '' || 
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.shortName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesLevel && matchesSearch;
  });

  const toggleProgram = (programId) => {
    setExpandedProgram(expandedProgram === programId ? null : programId);
  };

  return (
    <div data-testid="vocational-page" className="min-h-screen pt-20">
      <SEO {...pageSEO.vocational} url="/vocational" />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#C5A028]">
        <div className="absolute inset-0 pattern-overlay opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <span className="inline-block px-4 py-2 bg-[#0D4A2E] text-white text-sm font-semibold mb-4">
            THE INSTITUTE OF PROFESSIONAL STUDIES
          </span>
          <h1 className="text-5xl lg:text-6xl font-bold text-[#0D4A2E] mb-4">
            Vocational <span className="text-white">Programs</span>
          </h1>
          <p className="text-[#0D4A2E]/80 text-xl max-w-2xl">
            Certificate and diploma programs designed to equip you with practical skills 
            for immediate employment and career advancement.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 bg-[#0D4A2E] text-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-[#C5A028]">{vocationalStats.totalPrograms}</div>
              <div className="text-sm text-white/70">Total Programs</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#C5A028]">{vocationalStats.certificates}</div>
              <div className="text-sm text-white/70">Certificates</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#C5A028]">{vocationalStats.diplomas}</div>
              <div className="text-sm text-white/70">Diplomas</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-[#C5A028]">{vocationalStats.categories}</div>
              <div className="text-sm text-white/70">Categories</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-[#F5F5F5] border-b border-[#E5E5E5] sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Search */}
            <div className="relative max-w-md flex-grow">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666]" />
              <input
                type="text"
                placeholder="Search programs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="vocational-search"
                className="w-full pl-12 pr-4 py-3 border border-[#E5E5E5] bg-white focus:border-[#0D4A2E] focus:outline-none transition-colors duration-200"
              />
            </div>
            
            {/* Level Filter */}
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedLevel('all')}
                data-testid="filter-level-all"
                className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  selectedLevel === 'all'
                    ? 'bg-[#C5A028] text-[#1A1A1A]'
                    : 'bg-white text-[#666666] hover:bg-[#C5A028] hover:text-[#1A1A1A] border border-[#E5E5E5]'
                }`}
              >
                All Levels
              </button>
              <button
                onClick={() => setSelectedLevel('Certificate')}
                data-testid="filter-level-certificate"
                className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  selectedLevel === 'Certificate'
                    ? 'bg-[#C5A028] text-[#1A1A1A]'
                    : 'bg-white text-[#666666] hover:bg-[#C5A028] hover:text-[#1A1A1A] border border-[#E5E5E5]'
                }`}
              >
                Certificates
              </button>
              <button
                onClick={() => setSelectedLevel('Diploma')}
                data-testid="filter-level-diploma"
                className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  selectedLevel === 'Diploma'
                    ? 'bg-[#C5A028] text-[#1A1A1A]'
                    : 'bg-white text-[#666666] hover:bg-[#C5A028] hover:text-[#1A1A1A] border border-[#E5E5E5]'
                }`}
              >
                Diplomas
              </button>
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                data-testid="filter-category-all"
                className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                  selectedCategory === 'all'
                    ? 'bg-[#0D4A2E] text-white'
                    : 'bg-white text-[#666666] hover:bg-[#0D4A2E] hover:text-white border border-[#E5E5E5]'
                }`}
              >
                All Categories
              </button>
              {vocationalCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`filter-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-[#0D4A2E] text-white'
                      : 'bg-white text-[#666666] hover:bg-[#0D4A2E] hover:text-white border border-[#E5E5E5]'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programs List */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="mb-6 text-[#666666]">
            Showing {filteredPrograms.length} of {vocationalPrograms.length} programs
          </div>
          
          {filteredPrograms.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#666666] text-lg">No programs found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPrograms.map((program) => (
                <div
                  key={program.id}
                  data-testid={`vocational-program-${program.id}`}
                  className={`border transition-all duration-300 ${
                    expandedProgram === program.id 
                      ? 'border-[#C5A028] shadow-lg col-span-1 md:col-span-2 lg:col-span-3' 
                      : 'border-[#E5E5E5] hover:border-[#C5A028]/50'
                  }`}
                >
                  {/* Program Card Header */}
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleProgram(program.id)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-grow">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <span className={`px-3 py-1 text-xs font-bold ${
                            program.level === 'Diploma' 
                              ? 'bg-[#0D4A2E] text-white' 
                              : 'bg-[#C5A028] text-[#1A1A1A]'
                          }`}>
                            {program.level}
                          </span>
                          <span className="text-[#666666] text-sm">{program.category}</span>
                        </div>
                        <h3 className="text-xl font-bold text-[#0D4A2E] mb-2">{program.name}</h3>
                        <div className="flex items-center gap-4 text-sm text-[#666666]">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {program.duration}
                          </span>
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-4 h-4" />
                            {program.modules.length} Modules
                          </span>
                        </div>
                      </div>
                      <button className="p-2 flex-shrink-0">
                        {expandedProgram === program.id ? (
                          <ChevronUp className="w-6 h-6 text-[#0D4A2E]" />
                        ) : (
                          <ChevronDown className="w-6 h-6 text-[#666666]" />
                        )}
                      </button>
                    </div>
                    
                    {expandedProgram !== program.id && (
                      <p className="text-[#666666] text-sm mt-3 line-clamp-2">{program.description}</p>
                    )}
                  </div>

                  {/* Expanded Content */}
                  {expandedProgram === program.id && (
                    <div className="border-t border-[#E5E5E5] bg-[#F5F5F5] p-6">
                      <p className="text-[#666666] mb-6">{program.description}</p>
                      
                      <h4 className="font-bold text-[#0D4A2E] mb-4">Program Modules</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                        {program.modules.map((module, index) => (
                          <div 
                            key={index}
                            className="flex items-center gap-3 p-3 bg-white border-l-2 border-[#C5A028]"
                          >
                            <span className="w-6 h-6 bg-[#0D4A2E] text-white text-xs flex items-center justify-center flex-shrink-0">
                              {index + 1}
                            </span>
                            <span className="text-sm text-[#0D4A2E]">{module}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-wrap gap-4">
                        <a 
                          href="https://cnte.ciu.edu.lr/apply"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-6 py-3 font-semibold hover:bg-[#D4B43D] transition-colors duration-200"
                        >
                          <ArrowRight className="w-4 h-4" />
                          Apply for {program.shortName}
                        </a>
                        <Link 
                          to="/contact"
                          className="inline-flex items-center gap-2 border-2 border-[#0D4A2E] text-[#0D4A2E] px-6 py-3 font-semibold hover:bg-[#0D4A2E] hover:text-white transition-colors duration-200"
                        >
                          Request Info
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Why Vocational Training */}
      <section className="py-20 bg-[#0D4A2E] text-white pattern-overlay">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-center">
            Why Choose <span className="text-[#C5A028]">Vocational Training?</span>
          </h2>
          <p className="text-white/70 text-center mb-12 max-w-2xl mx-auto">
            Fast-track your career with practical, job-ready skills
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {[
              { icon: Clock, title: 'Quick Completion', desc: '3-12 months to complete' },
              { icon: Award, title: 'Recognized Certificates', desc: 'Industry-recognized qualifications' },
              { icon: BookOpen, title: 'Practical Focus', desc: 'Hands-on, job-ready training' },
              { icon: ArrowRight, title: 'Career Pathway', desc: 'Direct route to employment' },
            ].map((item, index) => (
              <div 
                key={index}
                className="p-8 text-center border border-white/10 hover:bg-white/5 transition-colors duration-300"
              >
                <item.icon className="w-10 h-10 text-[#C5A028] mx-auto mb-4" />
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#0D4A2E] mb-4">Ready to Start?</h2>
          <p className="text-[#666666] mb-8">
            Enroll in a vocational program and gain the skills you need for your career.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href="https://cnte.ciu.edu.lr/apply"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#C5A028] text-[#1A1A1A] px-8 py-4 font-bold hover:bg-[#D4B43D] transition-colors duration-200"
            >
              <ArrowRight className="w-5 h-5" />
              APPLY NOW
            </a>
            <Link 
              to="/programs"
              className="inline-flex items-center gap-2 border-2 border-[#0D4A2E] text-[#0D4A2E] px-8 py-4 font-bold hover:bg-[#0D4A2E] hover:text-white transition-colors duration-200"
            >
              VIEW DEGREE PROGRAMS
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
